import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { VStack } from 'tio-ui/components/layout';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import { gte } from 'tio-ui/utilities';
import GlassCard from 'tio-reimagined/components/glass-card';
import {
  PhSun,
  PhMoon,
  PhCloudSun,
  PhTrendDown,
  PhPiggyBank,
  PhGraduationCap,
  PhCaretRight,
  PhHandshake,
  PhCompass,
  PhBank,
  PhReceipt,
  PhChartLineUp,
  PhBuildings,
  PhHeart,
  PhShieldCheck,
  PhFileText,
  PhClipboardText,
  PhStamp,
} from 'ember-phosphor-icons';
import type SessionContextService from 'tio-employee/services/session-context';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';

import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import BenefitCard from 'tio-employee/components/reimagined/benefit-card';
import type { BenefitOpportunity } from 'tio-employee/components/reimagined/benefit-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import PageHero from 'tio-reimagined/components/page-hero';

/**
 * Reimagined dashboard rendered inline at the main /dashboard route.
 *
 * Follows the account-activity inline swap pattern: the legacy dashboard
 * template checks betaExperience.isEnabled and renders this component
 * instead of the widget grid. Navigation stays unchanged.
 */

interface ReimaginedDashboardSignature {
  Args: {
    model: ReimaginedHomeModel;
  };
}

interface UnifiedFinancials {
  totalLoans: number;
  employerHelpYTD: number;
  employerHelpLifetime: number;
  projectedForgiveness: number;
  taxSavingsYTD: number;
  yearsToPayoff: number;
}

export default class ReimaginedDashboard extends Component<ReimaginedDashboardSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare reimaginedData: ReimaginedDataService;
  @tracked showAllActions = false;

  // ── Data source helpers ─────────────────────────────────────────────

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  get studentLoansSource(): DataSource {
    return this.dataSourceInfo.studentLoans;
  }

  get section127Source(): DataSource {
    return this.dataSourceInfo.section127Used;
  }

  // ── User & employer context ─────────────────────────────────────────

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  // ── Feature eligibility ─────────────────────────────────────────────

  get financialSnapshot() {
    return this.args.model.financialSnapshot;
  }

  get pslfData() {
    return this.args.model.pslfData;
  }

  get tasData() {
    return this.args.model.tasData;
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get showStudentLoans() {
    return this.reimaginedData.showStudentLoanRepayment;
  }

  get showTuitionAssistance() {
    return this.reimaginedData.showTuitionAssistance;
  }

  get hasPslfForms() {
    return this.reimaginedData.showPslf && this.pslfData.forms.length > 0;
  }

  get pslfQualifyingPayments() {
    return Math.max(
      0,
      ...this.pslfData.accounts.flatMap((a) => a.loans.map((l) => l.qualifyingPayments ?? 0))
    );
  }

  get pslfYearsRemaining() {
    return Math.ceil((120 - this.pslfQualifyingPayments) / 12);
  }

  get pslfEstimatedForgiveness() {
    return this.pslfData.estimatorResults?.estimatedForgivenessAmount ?? 0;
  }

  get tasEnrolled() {
    return (
      this.tasData.programs.length > 0 ||
      this.tasData.applications.length > 0 ||
      this.tasData.summary.activeCount > 0
    );
  }

  get tasPendingApplications() {
    return this.tasData.summary.pendingCount;
  }

  // ── Admin role checks ───────────────────────────────────────────────

  get isPslfAdmin() {
    return !!this.sessionContext.roles?.find((e) => e.relationshipType === 'PSLF_ADMIN');
  }

  get isTasAdmin() {
    return !!this.sessionContext.roles?.some(
      (role) =>
        role.relationshipType.startsWith('TAS.Approver') ||
        role.relationshipType === 'TAS.Admin.SUPERUSER' ||
        role.relationshipType === 'TAS.Admin.PROGRAM_ADMIN'
    );
  }

  // ── Unified financials ──────────────────────────────────────────────

  get financials(): UnifiedFinancials {
    const loans = this.financialSnapshot.studentLoans;
    const contributions = this.financialSnapshot.employerContributions;

    return {
      totalLoans: loans.totalBalance,
      employerHelpYTD: contributions.yearToDate,
      employerHelpLifetime: contributions.lifetime,
      projectedForgiveness: this.hasPslfForms ? this.pslfEstimatedForgiveness : 0,
      taxSavingsYTD: this.financialSnapshot.optimizationPotential.taxSavingsYtd,
      yearsToPayoff: this.calculateYearsToPayoff(loans.estimatedPayoffDate),
    };
  }

  calculateYearsToPayoff(dateStr: string) {
    const payoffDate = new Date(dateStr);
    const now = new Date();
    const diffYears = (payoffDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 365);
    return Math.max(0, Math.round(diffYears * 10) / 10);
  }

  // ── Smart actions (only data-backed) ────────────────────────────────

  get smartActions(): SmartAction[] {
    const actions: SmartAction[] = [];
    const section127 = this.financialSnapshot.section127;

    // ── Admin actions (show first for admins) ───────────────────────

    if (this.isPslfAdmin) {
      actions.push({
        id: 'pslf-admin-review',
        priority: 'urgent',
        category: 'deadline',
        icon: PhStamp,
        title: 'PSLF forms awaiting your review',
        description: 'Employee certification forms need employer approval',
        cta: 'Review forms',
        route: 'authenticated.admin.pslf.forms',
      });
    }

    if (this.isTasAdmin) {
      actions.push({
        id: 'tas-admin-approvals',
        priority: 'urgent',
        category: 'deadline',
        icon: PhClipboardText,
        title: 'Tuition applications need approval',
        description: 'Review and approve pending tuition assistance requests',
        cta: 'Open approval queue',
        route: 'authenticated.admin.tas.approval-queue',
      });
    }

    // ── Employee actions ────────────────────────────────────────────

    if (section127.remaining > 2000) {
      actions.push({
        id: 'use-benefits',
        priority: 'urgent',
        category: 'money',
        icon: PhPiggyBank,
        title: `You have ${formatCurrency(section127.remaining)} in unused benefits`,
        description: "Tax-free employer money expires Dec 31 if you don't use it",
        impact: `Save ${formatCurrency(section127.remaining * 0.25)} in taxes`,
        cta: 'Use my benefits',
        route: 'authenticated.reimagined.finance',
      });
    }

    if (this.tasPendingApplications > 0) {
      const count = this.tasPendingApplications;
      actions.push({
        id: 'tas-pending',
        priority: 'high',
        category: 'money',
        icon: PhGraduationCap,
        title: `${count} tuition request${count > 1 ? 's' : ''} pending`,
        description: 'Check the status of your tuition assistance applications',
        cta: 'View status',
        route: 'authenticated.reimagined.benefits',
      });
    }

    if (this.hasPslfForms) {
      const payments = this.pslfQualifyingPayments;
      const remaining = 120 - payments;
      actions.push({
        id: 'pslf-cert',
        priority: remaining <= 12 ? 'urgent' : 'high',
        category: 'deadline',
        icon: PhFileText,
        title: remaining <= 12 ? 'Almost there! Keep certifying' : 'Annual PSLF certification due',
        description: 'Certify your qualifying payments to stay on track for forgiveness',
        impact: `${payments} of 120 payments certified`,
        cta: 'Certify now',
        route: 'authenticated.pslf',
      });
    }

    if (this.showStudentLoans && this.showTuitionAssistance) {
      const recommendation = this.financialSnapshot.optimizationPotential;
      if (recommendation.potentialSavings > 1000) {
        actions.push({
          id: 'optimize',
          priority: 'high',
          category: 'money',
          icon: PhChartLineUp,
          title: `Save ${formatCurrency(recommendation.potentialSavings)} more per year`,
          description: 'A better way to split your benefits between loans and tuition',
          impact: `Pay off ${recommendation.projectedPayoffAcceleration} faster`,
          cta: 'See optimization',
          route: 'authenticated.reimagined.finance.optimizer',
        });
      }
    }

    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    return actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }

  get visibleActions() {
    return this.showAllActions ? this.smartActions : this.smartActions.slice(0, 4);
  }

  toggleShowAll = () => {
    this.showAllActions = !this.showAllActions;
  };

  // ── Benefit opportunities ───────────────────────────────────────────

  get benefitOpportunities(): BenefitOpportunity[] {
    const opportunities: BenefitOpportunity[] = [];
    const section127 = this.financialSnapshot.section127;

    if (this.showStudentLoans) {
      opportunities.push({
        id: 'slr',
        title: 'Student Loan Payments',
        description: `${this.employerName} pays toward your student loans`,
        value: `$${section127.loanAllocation.toLocaleString()}/year`,
        enrolled: true,
        eligible: true,
        icon: PhBank,
      });
    }

    if (this.showTuitionAssistance) {
      opportunities.push({
        id: 'tas',
        title: 'Tuition Assistance',
        description: 'Get help paying for courses and certifications',
        value: `$${section127.tuitionAllocation.toLocaleString()}/year`,
        enrolled: this.tasEnrolled,
        eligible: true,
        icon: PhGraduationCap,
      });
    }

    if (this.hasPslfForms) {
      opportunities.push({
        id: 'pslf',
        title: 'Loan Forgiveness (PSLF)',
        description: 'Work at a qualifying employer = loans forgiven after 10 years',
        value: 'Up to 100% forgiven',
        enrolled: true,
        eligible: true,
        icon: PhShieldCheck,
      });
    }

    if (this.showTuitionAssistance || this.showStudentLoans) {
      opportunities.push({
        id: 'section127',
        title: 'Tax-Free Education Benefit',
        description: 'Up to $5,250/year in tax-free education assistance',
        value: `$${section127.remaining.toLocaleString()} remaining`,
        deadline: 'Dec 31',
        enrolled: true,
        eligible: true,
        icon: PhReceipt,
      });
    }

    return opportunities;
  }

  // ── Template ────────────────────────────────────────────────────────

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 max-w-5xl mx-auto gap-6 pb-8">

      {{! ─── HERO: Greeting + Financial KPIs ─────────────────────────── }}
      <DataIndicator
        @source={{this.studentLoansSource}}
        @showBadge={{true}}
        @dataSourceId="student-loans"
      >
        <PageHero @theme="teal" @decoration="dots" @rounded="2xl">
          <:default>
            <div class="flex items-center gap-2 text-white/80 mb-1">
              {{#let this.greetingIcon as |GreetingIcon|}}
                <GreetingIcon @size={{18}} />
              {{/let}}
              <span class="text-sm">{{this.greeting}}, {{this.firstName}}</span>
            </div>

            <h1 class="text-white text-2xl md:text-3xl font-bold mb-6">
              Your Benefits Dashboard
            </h1>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {{#if this.showStudentLoans}}
                <GlassCard
                  @icon={{PhBank}}
                  @label="Student Loans"
                  @value={{formatCurrency this.financials.totalLoans}}
                >
                  <:subtext>
                    <PhTrendDown @size={{14}} class="text-emerald-300" />
                    <span>{{this.financials.yearsToPayoff}} years to payoff</span>
                  </:subtext>
                </GlassCard>

                <GlassCard
                  @icon={{PhHandshake}}
                  @label="Employer Help YTD"
                  @value={{formatCurrency this.financials.employerHelpYTD}}
                  @accent="emerald"
                  @subtext="{{formatCurrency this.financials.employerHelpLifetime}} lifetime"
                />
              {{else}}
                <GlassCard
                  @icon={{PhGraduationCap}}
                  @label="Tuition Available"
                  @value={{formatCurrency this.section127Info.remaining}}
                  @subtext="Annual tax-free benefit"
                  @accent="emerald"
                />

                <GlassCard
                  @icon={{PhHandshake}}
                  @label="Employer Help YTD"
                  @value={{formatCurrency this.financials.employerHelpYTD}}
                  @accent="emerald"
                  @subtext="{{formatCurrency this.financials.employerHelpLifetime}} lifetime"
                />
              {{/if}}

              <GlassCard
                @icon={{PhReceipt}}
                @label="Tax Savings YTD"
                @value={{formatCurrency this.financials.taxSavingsYTD}}
                @accent="amber"
                @subtext="From Section 127 benefit"
              />

              {{#if this.hasPslfForms}}
                <GlassCard
                  @icon={{PhShieldCheck}}
                  @label="Projected Forgiveness"
                  @value={{formatCurrency this.financials.projectedForgiveness}}
                  @accent="violet"
                  @subtext="{{this.pslfQualifyingPayments}}/120 payments · {{this.pslfYearsRemaining}} years left"
                />
              {{else}}
                <GlassCard
                  @icon={{PhReceipt}}
                  @label="Section 127 Remaining"
                  @value={{formatCurrency this.financialSnapshot.section127.remaining}}
                  @accent="teal"
                  @subtext="Resets Dec 31"
                />
              {{/if}}
            </div>
          </:default>
        </PageHero>
      </DataIndicator>

      {{! ─── SMART ACTIONS ────────────────────────────────────────────── }}
      {{#if this.smartActions.length}}
        <div>
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <PhCompass @size={{24}} class="text-violet-600 dark:text-violet-400" />
              <h2 class="text-xl font-semibold text-gray-900">What to Do Next</h2>
            </div>
            <Text @variant="muted" class="text-sm">
              {{this.smartActions.length}}
              {{if (gte this.smartActions.length 2) "actions" "action"}}
            </Text>
          </div>

          <div class="space-y-3">
            {{#each this.visibleActions as |smartAction|}}
              <SmartActionCard @item={{smartAction}} />
            {{/each}}
          </div>

          {{#if (gte this.smartActions.length 5)}}
            <button
              type="button"
              class="w-full mt-3 py-3 text-center text-violet-600 hover:text-violet-700 font-medium text-sm flex items-center justify-center gap-2"
              {{on "click" this.toggleShowAll}}
            >
              {{#if this.showAllActions}}
                Show fewer
              {{else}}
                Show all
                {{this.smartActions.length}}
                actions
                <PhCaretRight @size={{16}} />
              {{/if}}
            </button>
          {{/if}}
        </div>
      {{/if}}

      {{! ─── YOUR BENEFITS ────────────────────────────────────────────── }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
      >
        <div class="bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-950/[0.04]">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <PhHeart @size={{24}} class="text-pink-500 dark:text-pink-400" />
              <h2 class="text-lg font-semibold text-gray-900">
                Your
                {{this.employerName}}
                Benefits
              </h2>
            </div>
            <Chip @intent="success" class="text-xs">
              {{this.benefitOpportunities.length}}
              active
            </Chip>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{#each this.benefitOpportunities as |benefit|}}
              <BenefitCard @benefit={{benefit}} />
            {{/each}}
          </div>
        </div>
      </DataIndicator>

      {{! ─── EMPLOYER APPRECIATION ────────────────────────────────────── }}
      {{#if this.financials.employerHelpLifetime}}
        <div class="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-white/20 rounded-lg">
              <PhBuildings @size={{28}} />
            </div>
            <div>
              <h3 class="text-white text-lg font-semibold">
                {{this.employerName}}
                has invested
                {{formatCurrency this.financials.employerHelpLifetime}}
                in you
              </h3>
              <p class="text-emerald-100">
                {{#if this.showStudentLoans}}
                  {{#if this.showTuitionAssistance}}
                    Through student loan payments, tuition assistance, and tax-free benefits
                  {{else}}
                    Through student loan payments and tax-free benefits
                  {{/if}}
                {{else}}
                  {{#if this.showTuitionAssistance}}
                    Through tuition assistance and tax-free benefits
                  {{else}}
                    Through tax-free benefits
                  {{/if}}
                {{/if}}
              </p>
            </div>
          </div>
        </div>
      {{/if}}
    </VStack>
  </template>
}
